interface LegacyMonitoringEventPayload {
  name: string;
  ext1?: string;
  ext2?: string;
  ext3?: string;
  ext4?: string;
  ext5?: string;
  ext6?: string;
  ext7?: string;
  ext8?: string;
  ext9?: string;
  ext10?: string;
  from?: string;
}

interface MonitoringEventPayload extends LegacyMonitoringEventPayload {
  page_url?: string;
}

export type MonitoringEvent = string | MonitoringEventPayload;
type LegacyMonitoringEvent = string | LegacyMonitoringEventPayload;

interface MonitoringSdkClient {
  reportEvent(event: MonitoringEvent): unknown;
  info(...messages: unknown[]): unknown;
  error(...messages: unknown[]): unknown;
}

interface LegacyMonitoringClient extends Omit<MonitoringSdkClient, 'reportEvent'> {
  reportEvent(event: LegacyMonitoringEvent): unknown;
  setConfig(config: { uin: string }): unknown;
}

interface GalileoMonitoringClient extends MonitoringSdkClient {
  setConfig(config: { uid: string }): unknown;
}

export interface MonitoringSink extends MonitoringSdkClient {
  setUser(userName: string): unknown;
}

export interface MonitoringReporter {
  reportEvent(event: MonitoringEvent): void;
  info(...messages: unknown[]): void;
  error(...messages: unknown[]): void;
  setUser(userName: string): void;
}

type MonitoringMethod = 'reportEvent' | 'info' | 'error' | 'setUser';
type MonitoringFailureHandler = (method: MonitoringMethod, error: unknown) => void;

function toLegacyMonitoringEvent(event: MonitoringEvent): LegacyMonitoringEvent {
  if (typeof event === 'string') return event;

  const { page_url: _pageUrl, ...legacyEvent } = event;
  return legacyEvent;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof value === 'object'
    && value !== null
    && 'then' in value
    && typeof value.then === 'function';
}

function defaultFailureHandler(method: MonitoringMethod, error: unknown): void {
  // 监控链路异常不能影响业务，也不能再次走监控形成递归。
  console.warn(['[monitoring] ', method, ' failed'].join(''), error);
}

export function createLegacyMonitoringSink(client: LegacyMonitoringClient): MonitoringSink {
  return {
    reportEvent: event => client.reportEvent(toLegacyMonitoringEvent(event)),
    info: (...messages) => client.info(...messages),
    error: (...messages) => client.error(...messages),
    setUser: userName => client.setConfig({ uin: userName }),
  };
}

export function createGalileoMonitoringSink(client: GalileoMonitoringClient): MonitoringSink {
  return {
    reportEvent: event => client.reportEvent(event),
    info: (...messages) => client.info(...messages),
    error: (...messages) => client.error(...messages),
    setUser: userName => client.setConfig({ uid: userName }),
  };
}

export function createMonitoringReporter(
  sinks: readonly MonitoringSink[],
  onFailure: MonitoringFailureHandler = defaultFailureHandler,
): MonitoringReporter {
  const broadcast = (method: MonitoringMethod, invoke: (sink: MonitoringSink) => unknown): void => {
    sinks.forEach((sink) => {
      try {
        const result = invoke(sink);
        if (isPromiseLike(result)) {
          void Promise.resolve(result).catch(error => onFailure(method, error));
        }
      } catch (error) {
        onFailure(method, error);
      }
    });
  };

  return {
    reportEvent(event) {
      broadcast('reportEvent', sink => sink.reportEvent(event));
    },
    info(...messages) {
      broadcast('info', sink => sink.info(...messages));
    },
    error(...messages) {
      broadcast('error', sink => sink.error(...messages));
    },
    setUser(userName) {
      broadcast('setUser', sink => sink.setUser(userName));
    },
  };
}
